import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Header } from "../components/Header";
import { TipsModal } from "../components/TipsModal";
import { OrdersTab } from "../components/OrdersTab";
import { DataEntryTab } from "../components/DataEntryTab";
import { SearchTab } from "../components/SearchTab";
import { FormsTab } from "../components/FormsTab";
import { FilmDropTab } from "../components/FilmDropTab";
import { ChangeHistoryModal, type ChangeHistoryTarget } from "../components/ChangeHistoryModal";
import { FunctionsModal, type ActionsPanel } from "../components/FunctionsModal";
import { TasksModal } from "../components/TasksModal";
import { TaskDetailModal } from "../components/TaskDetailModal";
import { AISearchModal } from "../components/AISearchModal";
import { QuickSearchModal } from "../components/QuickSearchModal";
import { useCounts, tasksBubbleLabel } from "../hooks/useCounts";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";
import type { TaskItem } from "../lib/types";

export const Route = createFileRoute("/")({
  component: HomePage,
});

type Tab = "search" | "orders" | "dataEntry" | "forms" | "filmDrop";

function HomePage() {
  const [tab, setTab] = useState<Tab>("orders");
  const [tipsOpen, setTipsOpen] = useState(false);
  const [history, setHistory] = useState<ChangeHistoryTarget>(null);
  const [actions, setActions] = useState<{ open: boolean; panel: ActionsPanel }>({ open: false, panel: "clubMember" });
  const [tasksOpen, setTasksOpen] = useState(false);
  const [taskDetail, setTaskDetail] = useState<TaskItem | null>(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [searchInject, setSearchInject] = useState<{ q: string; n: number } | undefined>(undefined);

  const { data: counts } = useCounts();
  const bubble = tasksBubbleLabel(counts);

  const runSearchQuery = (q: string) => {
    setTab("search");
    setSearchInject((prev) => ({ q, n: (prev?.n ?? 0) + 1 }));
  };

  useKeyboardShortcuts({
    currentTab: tab,
    onTab: (t) => setTab(t),
    onQuickSearch: () => setQuickOpen(true),
    onAI: () => setAiOpen(true),
    onEscape: () => {
      setTipsOpen(false);
      setQuickOpen(false);
      setAiOpen(false);
      setActions((a) => ({ ...a, open: false }));
      setTasksOpen(false);
    },
  });

  return (
    <>
      <Header
        variant="main"
        tasksLabel={bubble.label}
        tasksZero={bubble.zero}
        onOpenTips={() => setTipsOpen(true)}
        onOpenActions={(panel) => setActions({ open: true, panel: panel as ActionsPanel })}
        onOpenTasks={() => setTasksOpen(true)}
      />

      <div className="tabs">
        <button className={`tab-button${tab === "search" ? " active" : ""}`} onClick={() => setTab("search")}>
          Search
        </button>
        <button className={`tab-button${tab === "orders" ? " active" : ""}`} onClick={() => setTab("orders")}>
          Orders
        </button>
        <button className={`tab-button${tab === "dataEntry" ? " active" : ""}`} onClick={() => setTab("dataEntry")}>
          Data Entry
        </button>
        <button className={`tab-button${tab === "forms" ? " active" : ""}`} onClick={() => setTab("forms")}>
          Forms
        </button>
        <button className={`tab-button${tab === "filmDrop" ? " active" : ""}`} onClick={() => setTab("filmDrop")}>
          Film Drop
        </button>
      </div>

      <SearchTab active={tab === "search"} runQuery={searchInject} />
      <OrdersTab active={tab === "orders"} onOpenHistory={(id, name) => setHistory({ id, name })} />
      <DataEntryTab active={tab === "dataEntry"} />
      <FormsTab active={tab === "forms"} />
      <FilmDropTab active={tab === "filmDrop"} onOpenHistory={(id, name) => setHistory({ id, name })} />

      <ChangeHistoryModal target={history} onClose={() => setHistory(null)} />
      <TipsModal open={tipsOpen} onClose={() => setTipsOpen(false)} />
      <FunctionsModal
        open={actions.open}
        panel={actions.panel}
        onClose={() => setActions((a) => ({ ...a, open: false }))}
        onPanelChange={(panel) => setActions({ open: true, panel })}
      />
      <TasksModal
        open={tasksOpen}
        onClose={() => setTasksOpen(false)}
        onOpenDetail={(t) => {
          setTasksOpen(false);
          setTaskDetail(t);
        }}
      />
      <TaskDetailModal task={taskDetail} onClose={() => setTaskDetail(null)} />
      <AISearchModal open={aiOpen} onClose={() => setAiOpen(false)} />
      <QuickSearchModal open={quickOpen} onClose={() => setQuickOpen(false)} onRunQuery={runSearchQuery} />

      <div id="loadingOverlay" style={{ display: "none" }}>
        <img src="https://downloads.atxfilmlab.com/loading.gif" alt="Loading..." id="loadingGif" />
      </div>
    </>
  );
}
